// Centralised axios instance + AXIOM API methods.
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "axiom_token";
const CODENAME_KEY = "axiom_codename";
const ADMIN_TOKEN_KEY = "axiom_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(CODENAME_KEY);
};

export const getCodename = () => localStorage.getItem(CODENAME_KEY);
export const setCodename = (c) => localStorage.setItem(CODENAME_KEY, c);

export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
export const setAdminToken = (t) => localStorage.setItem(ADMIN_TOKEN_KEY, t);
export const clearAdminToken = () => localStorage.removeItem(ADMIN_TOKEN_KEY);

const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const token = getToken();
  if (token && !cfg.headers.Authorization) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

export const adminApi = axios.create({ baseURL: API });
adminApi.interceptors.request.use((cfg) => {
  const token = getAdminToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// -------- auth --------
export const startAnonymousSession = async () => {
  const { data } = await axios.post(`${API}/auth/anonymous`);
  setToken(data.token);
  setCodename(data.codename);
  return data;
};

export const ensureSession = async () => {
  if (getToken()) return { codename: getCodename(), token: getToken() };
  return await startAnonymousSession();
};

export const getMe = async () => (await api.get("/auth/me")).data;

export const setPassphrase = async (passphrase) =>
  (await api.post("/auth/set-passphrase", { passphrase })).data;

export const recover = async (codename, passphrase) => {
  const { data } = await axios.post(`${API}/auth/recover`, {
    codename,
    passphrase,
  });
  setToken(data.token);
  setCodename(data.codename);
  return data;
};

// -------- theories --------
export const listTheories = async () => (await api.get("/theories")).data;
export const getTheory = async (slug) => (await api.get(`/theories/${slug}`)).data;
export const voteCredibility = async (slug, score) =>
  (await api.post(`/theories/${slug}/credibility`, { score })).data;
export const recentVotes = async (limit = 5) =>
  (await axios.get(`${API}/theories/recent-votes`, { params: { limit } })).data;
export const credibilityHistory = async (slug, days = 30) =>
  (await axios.get(`${API}/theories/${slug}/credibility-history`, { params: { days } })).data;

// -------- comments --------
export const listComments = async (slug, sort = "hot") =>
  (await api.get(`/theories/${slug}/comments`, { params: { sort } })).data;
export const postComment = async (slug, body, parent_id = null) =>
  (await api.post(`/theories/${slug}/comments`, { body, parent_id })).data;
export const voteComment = async (commentId, direction) =>
  (await api.post(`/comments/${commentId}/vote`, { direction })).data;

// -------- payments --------
export const createCheckout = async (plan) => {
  const origin = window.location.origin;
  const { data } = await api.post("/payments/checkout", {
    plan,
    origin_url: origin,
  });
  return data;
};
export const getPaymentStatus = async (sessionId) =>
  (await api.get(`/payments/status/${sessionId}`)).data;
export const cancelSubscription = async () =>
  (await api.post("/payments/cancel")).data;

// -------- evidence --------
export const listEvidence = async (slug) =>
  (await api.get(`/theories/${slug}/evidence`)).data;
export const getEvidence = async (hash) =>
  (await api.get(`/evidence/${hash}`)).data;
export const submitEvidence = async (slug, payload) =>
  (await api.post(`/theories/${slug}/evidence`, payload)).data;
export const voteEvidence = async (hash, vote) =>
  (await api.post(`/evidence/${hash}/vote`, { vote })).data;
export const flagEvidence = async (hash, reason) =>
  (await api.post(`/evidence/${hash}/flag`, { reason })).data;

// -------- admin --------
export const adminLogin = async (username, password) => {
  const { data } = await axios.post(`${API}/admin/login`, {
    username,
    password,
  });
  setAdminToken(data.token);
  return data;
};
export const adminStats = async () => (await adminApi.get("/admin/stats")).data;
export const adminComments = async () =>
  (await adminApi.get("/admin/comments")).data;
export const adminDeleteComment = async (id) =>
  (await adminApi.delete(`/admin/comments/${id}`)).data;
export const adminDossierStats = async () =>
  (await adminApi.get("/admin/dossiers/stats")).data;
export const adminActivity = async (limit = 50) =>
  (await adminApi.get(`/admin/activity?limit=${limit}`)).data;
export const adminSeedSync = async () =>
  (await adminApi.post("/admin/seed/sync")).data;

export default api;
